<template>
	<BaseCatalogBoughtTogether :items="props.items" :meta="props.meta" v-slot="{total, saving, addToCartData}" v-model="checkedItems">
		<div v-if="items.length" class="cd-bt-cnt">
			<div class="cd-bt-title"><BaseCmsLabel code="bought_together_title" /></div>
			<div class="cd-bt-items">
				<div class="cd-bt-item" v-for="(item, index) in items" :key="item.id">
					<div v-if="index != 0" class="cd-bt-item-plus"></div>
					<div class="cd-bt-item-image">
						<NuxtLink :to="item.url_without_domain">
							<BaseUiImage loading="lazy" :data="item.main_image_thumbs?.['width120-height120']" default="/images/no-image-120.jpg" :title="item.main_image_title" :alt="item.main_image_description ? item.main_image_description : item.title" />
							<div v-if="item.discount_percent_custom > 0" class="cd-bt-item-discount">-{{item.discount_percent_custom}}%</div>
						</NuxtLink>
					</div>
					<div class="cd-bt-item-right">
						<div class="cd-bt-item-content">
							<FeedbackRates v-if="item?.feedback_rate_widget?.rates > 0" :rates="item.feedback_rate_widget.rates" />
							<NuxtLink :to="item.url_without_domain" class="cd-bt-item-title">{{item.title}}</NuxtLink>
						</div>
						
						<div class="cd-bt-item-price-cnt">
							<div class="cd-bt-item-price-inner">
								<div :class="['cd-bt-item-price', (item.discount_percent > 0 || item.price_custom < item.basic_price) && 'with-discount']"><BaseUtilsFormatCurrency :price="item.price_custom" /></div>
								<div v-if="item.extra_price_lowest && (item.discount_percent || item.discount_percent_custom) && item.extra_price_lowest != 0" class="cd-bt-item-price-lowest"><BaseCmsLabel code="extra_price_lowest" />: <BaseUtilsFormatCurrency :price="item.extra_price_lowest" /></div>
							</div>
							<template v-if="item.is_available">
								<div class="cd-bt-item-add">
									<input type="checkbox" :name="item.shopping_cart_code" :value="item" :id="item.shopping_cart_code" v-model="checkedItems" :disabled="index == 0" />
									<label :for="item.shopping_cart_code"><BaseCmsLabel code="want_this" /></label>
								</div>
							</template>							
						</div>
					</div>
				</div>
			</div>
            <div class="cd-bt-footer" v-if="checkedItems?.length">
                <div class="cd-bt-footer-left" :class="{'is-disabled': checkedItems?.length == 1}">
                    <div class="cd-bt-chosen"><BaseCmsLabel code="choosen" />: <strong>{{checkedItems.length}}/{{items.length}}</strong></div>
                    <div class="cd-bt-chosen-price"><BaseCmsLabel code="total" />: <strong><BaseUtilsFormatCurrency :price="total" /></strong></div>
                    <div class="cd-bt-total-saving" v-if="saving"><strong><BaseCmsLabel code="price_save_2" /> <BaseUtilsFormatCurrency :price="saving" /></strong></div>
                </div>
                <BaseWebshopAddToCart v-slot="{onAddToCart, loading}" :data="addToCartData" :redirect="true" :add-to-cart-modal="false">
                    <div class="btn btn-green cd-bt-chosen-add" :class="{'loading': loading}" @click="onAddToCart"><UiLoader v-if="loading" /><span class="label-1"><BaseCmsLabel code="bought_together_to_cart" /></span><span class="label-2"><BaseCmsLabel code="bought_together_to_cart_2" /></span></div>
                </BaseWebshopAddToCart>
            </div>
		</div>
	</BaseCatalogBoughtTogether>
</template>

<script setup>
	const props = defineProps(['items', 'meta']);
	const checkedItems = ref([]);
</script>

<style lang="less" scoped>
    .cd-bt-title{
		font-size: 30px; line-height: 39px; font-weight: bold; padding: 55px 0 12px;
		@media (max-width: @tp){font-size: 24px; line-height: 30px; padding: 40px 0 9px;}
	}

	.cd-bt-items{
		@media (max-width: @m){width: calc(~'100% - -30px'); margin-left: -15px;}
	}
    
	.cd-bt-item{
		padding: 24px; border: 1px solid #DFDFDF; margin-top: -1px; display: flex; position: relative;
		@media (max-width: @m){padding: 15px; border-left: 0; border-right: 0;}
	}
	.cd-bt-item-plus{
		width: 32px; aspect-ratio: 1; position: absolute; top: -16px; left: 60px; background: #fff; border-radius: 100%; border: 1px solid #DFDFDF;
		@media (max-width: @tp){left: 43px;}
		@media (max-width: @m){width: 24px; top: -12px;}
		&:before, &:after{
			.pseudo(14px,2px); top: 50%; left: 50%; margin: -1px 0 0 -7px; background: var(--primaryColor);
			@media (max-width: @m){width: 10px; margin-left: -5px;}
		}
		&:after{.rotate(90deg);}
	}
    .cd-bt-item-image{
		flex: 0 0 104px; margin-right: 24px; position: relative;
		@media (max-width: @tp){flex: 0 0 80px; margin-right: 16px;}
        :deep(img){display: block; width: auto; height: auto; max-height: 100%; max-width: 100%;}
    }
	.cd-bt-item-discount{
		font-size: 18px; line-height: 22px; position: absolute; top: 0; left: 0; font-weight: normal; color: var(--textColor);
		@media (max-width: @tp){font-size: 16px;}
	}

	.cd-bt-item-right{
		display: flex; align-items: center; flex: 1 1 auto;
		@media (max-width: @m){display: block;}
	}

	.cd-bt-item-content{
		flex: 1 1 auto; padding-right: 24px;
		@media (max-width: @m){padding-right: 0;}
	}

	:deep(.cp-rate){justify-content: flex-start;}
	.cd-bt-item-title{
		font-size: 18px; line-height: 22px; font-weight: normal; color: var(--textColor); display: block; text-decoration: none; padding-top: 4px;
		@media (max-width: @tp){font-size: 16px; line-height: 20px;}
	}

	.cd-bt-item-price-cnt{
		text-align: right;
		@media (max-width: @m){text-align: left; display: flex; align-items: center; justify-content: space-between; margin-top: 10px;}
	}
	.cd-bt-item-price{
		font-size: 18px; line-height: 20px; font-weight: normal;
		@media (max-width: @tp){font-size: 16px;}
		&.with-discount{font-weight: bold; color: var(--primaryColor);}
	}	
	.cd-bt-item-price-lowest{
		font-size: 12px; line-height: 14px; color: #98989F; max-width: 120px; margin-left: auto; padding-top: 2px;
		@media (max-width: @tp){font-size: 11px; line-height: 12px; max-width: 105px;}
		@media (max-width: @m){margin: 0;}
	}

	.cd-bt-item-add{
		margin-top: 8px;
		@media (max-width: @m){margin: 0;}
	}
	input[type=checkbox]+label{
		border-radius: 2px; border: 1px solid #DFDFDF; height: 48px; display: flex; align-items: center; font-size: 18px; line-height: 21px; font-weight: normal; padding: 0 13px; white-space: nowrap;
		@media (max-width: @tp){height: 40px; font-size: 14px; padding: 0 10px;}
		&:before{position: static; margin-right: 12px;}
	}
	input[type=checkbox]:checked+label{border-color: var(--primaryColor);}
	input:disabled+label{opacity: 0.3; color: var(--textColor);}

	
	.cd-bt-footer{
		display: flex; align-items: center; justify-content: space-between; padding: 24px 0 15px;
		@media (max-width: @m){padding-top: 15px;}
	}
	.cd-bt-footer-left{
		font-size: 16px; line-height: 21px;
		@media (max-width: @tp){font-size: 14px; line-height: 18px;}
	}
	.cd-bt-chosen-price{
		font-size: 20px; line-height: 26px;
		@media (max-width: @tp){font-size: 16px; line-height: 21px;}
	}
	.cd-bt-total-saving{color: var(--red);}

	.cd-bt-chosen-add{
		cursor: pointer; margin: 0; padding: 0 45px;
		@media (max-width: @tp){padding: 0 16px;}
		@media (max-width: @m){min-width: 0; font-size: 16px; height: 50px;}
		@media (max-width: 350px){font-size: 14px;}
		span{
			position: relative; padding-left: 40px;
			@media (max-width: @tp){padding-left: 0;}
			&:before{
				.icon-cart; font: 24px/24px var(--fonti); top: 0; left: 0; position: absolute; display: block; color: white;
				@media (max-width: @tp){display: none;}
			}
		}
		.label-1{
			@media (max-width: @m){display: none;}
		}
		.label-2{
			display: none;
			@media (max-width: @m){display: inline;}
		}
	}	
</style>